/**
 * "Is this typed email an obvious fake?" — asked of floe-api's
 * POST /sites/email-check, the same verdict the voice agent and the claim
 * writer use (placeholder names like test123@, throwaway domains, domains that
 * cannot receive mail).
 *
 * Every failure answers "plausible": this only spares a visitor from starting a
 * demo on a fake address, and floe-api re-checks every address it stores.
 */
export interface EmailCheckResult {
  plausible: boolean;
}

export type EmailCheck = (email: string) => Promise<EmailCheckResult>;

const CHECK_TIMEOUT_MS = 3000;

const requestEmailCheck = async (
  apiUrl: string,
  clientKey: string,
  email: string,
): Promise<EmailCheckResult> => {
  try {
    const response = await fetch(`${apiUrl}/sites/email-check`, {
      body: JSON.stringify({ clientKey, email }),
      // Anonymous (clientKey + Origin), like validate-client-key.
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      signal: AbortSignal.timeout(CHECK_TIMEOUT_MS),
    });
    if (!response.ok) {
      return { plausible: true };
    }
    const body: { plausible?: boolean } = await response.json();
    return { plausible: body.plausible !== false };
  } catch {
    return { plausible: true };
  }
};

/** One checker per SDK instance; answers are memoized per address. */
export const createEmailCheck = (apiUrl: string, clientKey: string): EmailCheck => {
  const verdicts = new Map<string, Promise<EmailCheckResult>>();
  return (email) => {
    const key = email.trim().toLowerCase();
    let verdict = verdicts.get(key);
    if (!verdict) {
      verdict = requestEmailCheck(apiUrl, clientKey, key);
      verdicts.set(key, verdict);
    }
    return verdict;
  };
};
